import { useId, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IEcgRecording } from "../../@types/Ecg";
import {
  ECG_COLORS,
  MAJOR_MM,
  MINOR_MM,
  PX_PER_MM,
  ROW_HEIGHT_MM,
} from "./ecgChart.constants";
import {
  GridScrollArea,
  LeadLabelCell,
  LeadLabelColumn,
  ScrollableTrace,
} from "./EcgChart.styles";

export interface IEcgChartProps {
  recording: IEcgRecording;
  paperSpeed: number; // mm/s
  amplitudeGain: number; // mm/mV
  zoom: number; // multiplier
}

export function EcgChart({
  recording,
  paperSpeed,
  amplitudeGain,
  zoom,
}: IEcgChartProps) {
  const { t } = useTranslation();

  // Unique pattern ids so multiple charts on a page don't collide.
  // Strip the colons React's useId emits so they're safe inside url(#...).
  const rawId = useId().replace(/:/g, "");
  const minorId = `ecg-minor-${rawId}`;
  const majorId = `ecg-major-${rawId}`;

  // mm -> px conversions for the current scale settings.
  const pxPerMm = PX_PER_MM * zoom;
  const pxPerSecond = paperSpeed * pxPerMm;
  const pxPerMv = amplitudeGain * pxPerMm;

  const minorPx = MINOR_MM * pxPerMm;
  const majorPx = MAJOR_MM * pxPerMm;

  const rowHeightPx = ROW_HEIGHT_MM * pxPerMm;
  const totalHeightPx = rowHeightPx * recording.leads.length;

  // The trace fills the full width: it starts at the first sample and ends at
  // the last, with no leading/trailing margin (matches the AliveCor viewer).
  const signalStartX = 0;

  const traceWidthPx = recording.durationSeconds * pxPerSecond;
  const svgWidth = traceWidthPx;

  // Build the trace polyline for each lead.
  const leadGeometry = useMemo(
    () =>
      recording.leads.map((lead, index) => {
        const baseY = index * rowHeightPx + rowHeightPx / 2;

        const points = lead.samplesMv
          .map((mv, i) => {
            const x = signalStartX + (i / recording.frequencyHz) * pxPerSecond;
            const y = baseY - mv * pxPerMv;

            return `${x.toFixed(1)},${y.toFixed(1)}`;
          })
          .join(" ");

        return { key: lead.key, points };
      }),
    [recording, pxPerSecond, pxPerMv, rowHeightPx, signalStartX, pxPerMm]
  );

  return (
    <GridScrollArea>
      <LeadLabelColumn>
        {recording.leads.map((lead) => (
          <LeadLabelCell key={lead.key} style={{ height: rowHeightPx }}>
            {lead.label}
          </LeadLabelCell>
        ))}
      </LeadLabelColumn>

      <ScrollableTrace>
        <svg
          width={svgWidth}
          height={totalHeightPx}
          viewBox={`0 0 ${svgWidth} ${totalHeightPx}`}
          role="img"
          aria-label={t("patientPage_ecg_traceLabel")}
          shapeRendering="geometricPrecision"
        >
          <defs>
            <pattern
              id={minorId}
              width={minorPx}
              height={minorPx}
              patternUnits="userSpaceOnUse"
            >
              <path
                d={`M ${minorPx} 0 L 0 0 L 0 ${minorPx}`}
                fill="none"
                stroke={ECG_COLORS.minorLine}
                strokeWidth={0.6}
              />
            </pattern>
            <pattern
              id={majorId}
              width={majorPx}
              height={majorPx}
              patternUnits="userSpaceOnUse"
            >
              <path
                d={`M ${majorPx} 0 L 0 0 L 0 ${majorPx}`}
                fill="none"
                stroke={ECG_COLORS.majorLine}
                strokeWidth={1.1}
              />
            </pattern>
          </defs>

          {/* Paper + grid */}
          <rect
            width={svgWidth}
            height={totalHeightPx}
            fill={ECG_COLORS.paper}
          />
          <rect
            width={svgWidth}
            height={totalHeightPx}
            fill={`url(#${minorId})`}
          />
          <rect
            width={svgWidth}
            height={totalHeightPx}
            fill={`url(#${majorId})`}
          />

          {/* Lead traces */}
          {leadGeometry.map((lead) => (
            <polyline
              key={lead.key}
              points={lead.points}
              fill="none"
              stroke={ECG_COLORS.trace}
              strokeWidth={1.2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}
        </svg>
      </ScrollableTrace>
    </GridScrollArea>
  );
}
